/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import isEqual from 'lodash/isEqual';

import { action } from '@ember/object';
import { guidFor } from '@ember/object/internals';
import { inject as service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import type { IntlService } from 'ember-intl';
import type { Dropdown } from 'ember-basic-dropdown/components/basic-dropdown';
import type { PowerSelectArgs, Select } from 'ember-power-select/components/power-select';
import type { BvLabelledSignature, ILabelAction } from 'volta/components/bv-labelled';
import type { TSize } from 'volta/utils/style-utils';
import type { BvBtnArgs } from './../bv-btn/index';

import type { ContentValue } from '@glint/template';
import InfinityModel from 'ember-infinity/lib/infinity-model';

type ComponentArgs = Pick<
  BvLabelledSignature['Args'],
  | 'error'
  | 'warning'
  | 'helpText'
  | 'isEdited'
  | 'isRequired'
  | 'label'
  | 'labelHidden'
  | 'labelWrapperClass'
> &
  Omit<PowerSelectArgs, 'onChange' | 'onFocus' | 'onBlur' | 'options'>;

export interface BvSelectMultiArgs extends ComponentArgs {
  allowClear?: boolean;
  bufferSize?: number;
  dataName?: string;
  defaultSize?: 'md';
  disabled?: boolean;
  dropdownClass?: string;
  dropdownSize?: TSize;
  extra?: ContentValue;
  fill?: boolean;
  focused?: boolean;
  id?: string;
  infiniteScroll?: boolean;
  isLoading?: boolean;
  labelAction?: ILabelAction;
  labelClass?: string;
  labelInfo?: ContentValue;
  labelInline?: boolean;
  opened?: boolean;
  options?: any[] | PromiseProxy<any[]> | InfinityModel;
  optionHeight?: number;
  optionKey?: string;
  optionPath?: string;
  resourceNamePlural?: string;
  searchEnabled?: boolean;
  selectedPath?: string;
  size?: BvBtnArgs['size'];
  tabIndex?: number;
  renderAll?: boolean;
  onKeyDown?: (event: KeyboardEvent) => boolean;
  // Callback when selection is changed
  onChange?: (selection: any[], select: Select, event?: Event) => void;
  // Callback when select is focused
  onFocus?: (select: Select, event: FocusEvent) => void;
  // Callback when focus is removed
  onBlur?: (select: Select, event: FocusEvent) => void;
}

interface BvSelectMultiSignature {
  Args: BvSelectMultiArgs;
  Blocks: {
    default: [any, Dropdown];
  };
  Element: HTMLElement;
}

export default class BvSelectMultiComponent extends Component<BvSelectMultiSignature> {
  @service intl!: IntlService;

  id = this.args.id ?? `BvSelectMulti-${guidFor(this)}`;

  get searchEnabled() {
    return this.args.searchEnabled ?? true;
  }

  get infinityModel() {
    return this.args.options instanceof InfinityModel && this.args.infiniteScroll
      ? this.args.options
      : undefined;
  }

  get powerSelectOptions() {
    return this.args.options as any[] | PromiseProxy<any[]> | undefined;
  }

  @tracked focused = Boolean(this.args.focused);

  // Actions
  // ~~~~~

  @action
  onChange(selection: any[], select: Select, event?: Event) {
    const { selected = [], highlighted = [] } = select;

    this.args.onChange?.(
      // Because we force the inclusion if selectedIds sometimes, we need to filter out the highlighted
      isEqual(selected, highlighted)
        ? selection.filter((o) => !isEqual(highlighted, o))
        : selection,
      select,
      event
    );
  }

  @action
  onFocus(select: Select, event: FocusEvent) {
    this.focused = true;
    this.args.onFocus?.(select, event);
  }

  @action
  onBlur(select: Select, event: FocusEvent) {
    this.focused = false;
    this.args.onBlur?.(select, event);
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    BvSelectMulti: typeof BvSelectMultiComponent;
    'bv-select-multi': typeof BvSelectMultiComponent;
  }
}
